import React from "react";
import { SectionLabel, Reveal, CTAButton, SunRays } from "@/lib/uiHelpers";
import { LANE_REVIEW } from "@/constants/landingCopy";
import { Star, BadgeCheck, Quote, ExternalLink } from "lucide-react";

function Stars({ count = 5 }) {
  return (
    <span className="inline-flex items-center gap-0.5" aria-label={`${count} star rating`}>
      {Array.from({ length: count }).map((_, i) => (
        <Star key={`fr-star-${i}`} size={18} fill="#F5A623" stroke="#F5A623" />
      ))}
    </span>
  );
}

function ReviewCard({ r }) {
  return (
    <div className="rounded-3xl bg-white p-7 md:p-10" style={{ boxShadow: "0 34px 80px -32px rgba(0,0,0,0.65)" }}>
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <Stars count={r.rating} />
        <span className="inline-flex items-center gap-1.5 text-sm font-semibold" style={{ color: "#27AE60" }}>
          <BadgeCheck size={17} /> Verified Google Review
        </span>
      </div>

      <div className="mt-5 relative">
        <Quote size={44} className="absolute -top-3 -left-1" style={{ color: "#1A2B4A", opacity: 0.08 }} />
        <p className="font-body relative text-lg md:text-2xl leading-relaxed" style={{ color: "#2C3E50" }}>
          &ldquo;{r.quote}&rdquo;
        </p>
      </div>

      <div className="mt-7 flex items-center gap-4">
        <div
          className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full font-head font-extrabold text-lg"
          style={{ background: "#1A2B4A", color: "#F5A623" }}
          aria-hidden="true"
        >
          LM
        </div>
        <div>
          <div className="font-head font-extrabold text-lg" style={{ color: "#1A2B4A" }}>{r.name}</div>
          <div className="font-body text-sm" style={{ color: "#2C3E50" }}>{r.title}</div>
          <div className="font-body text-xs mt-0.5" style={{ color: "#7F8C8D" }}>{r.meta}</div>
          <a
            href={r.website}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-sm font-semibold mt-1 hover:underline"
            style={{ color: "#F5A623" }}
          >
            {r.websiteLabel} <ExternalLink size={13} />
          </a>
        </div>
      </div>

      <div className="mt-6 rounded-2xl p-5" style={{ background: "#F4F0E8", borderLeft: "4px solid #F5A623" }}>
        <div className="flex items-center gap-1.5 font-head font-bold text-sm" style={{ color: "#1A2B4A" }}>
          STARPOWER Network <BadgeCheck size={15} style={{ color: "#3897f0" }} />
          <span className="font-body font-normal text-xs" style={{ color: "#7F8C8D" }}>(Owner response)</span>
        </div>
        <p className="font-body text-sm mt-2" style={{ color: "#2C3E50" }}>{r.ownerReply}</p>
      </div>
    </div>
  );
}

export default function FeaturedReview() {
  const r = LANE_REVIEW;
  return (
    <section className="section-pad relative overflow-hidden" style={{ backgroundColor: "#1A2B4A" }}>
      <SunRays color="#F5A623" opacity={0.1} originX="0%" originY="0%" />
      <div className="container-max relative z-10">
        <Reveal className="text-center max-w-2xl mx-auto">
          <SectionLabel>Featured Review</SectionLabel>
          <h2 className="font-head font-extrabold text-3xl md:text-5xl text-white">
            Trusted by a 40-Year Master Builder
          </h2>
          <p className="font-body mt-4 text-base md:text-lg text-white/75">
            When a luxury home builder with four decades of experience and famously high standards
            picks our team for his own West Maui home &mdash; and recommends us &mdash; that says it all.
          </p>
        </Reveal>

        <Reveal delay={0.12} className="mt-12 max-w-3xl mx-auto">
          <ReviewCard r={r} />
        </Reveal>

        <div className="mt-10 text-center">
          <CTAButton testId="featured-review-cta" variant="gold" className="w-full sm:w-auto">
            &rarr; Work With Maui&apos;s Trusted Team &mdash; Book My Call
          </CTAButton>
        </div>
      </div>
    </section>
  );
}
